import {
  Box,
  ExternalLink,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@alice-financial/pretext-ui'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import * as React from 'react'
import { useIsFinchOnboardingEnabledForPlatform } from '../../../externalServices/finch/useFinchIframe'
import { useOrg } from '../../organization/useOrg'
import { EnabledFinchSetup } from '../../payroll/FinchSetup'
import { PayrollConnectionInputs } from '../../payroll/PayrollConnectionInputs'

const PayrollConnectionHeading = () => {
  return (
    <Typography variant="h3" color="text.secondary" gutterBottom>
      Grant payroll processing permission
    </Typography>
  )
}
export const PayrollStepTitle = 'Payroll connection'

export const PayrollDescription = () => (
  <Typography variant="body2" color="text.secondary">
    Connect your payroll system to Alice
  </Typography>
)

// Wrapper component that uses the core payroll components
export const PayrollComponent = () => {
  const org = useOrg()
  const finchConnectEnabled = useIsFinchOnboardingEnabledForPlatform(org)

  if (!org) return null
  const isPayrollConnected = org.isOnboarded || Boolean(org.payrollConnection.payrollConnectedAt)

  return (
    <>
      <Grid container spacing={3} flexDirection="column">
        {finchConnectEnabled && (
          <Grid container flexWrap="nowrap" borderBottom={1} borderColor="divider" py={2}>
            <Typography variant="h1" component="p" fontSize="3.5rem" fontWeight="bolder">
              1
            </Typography>
            <Grid>
              <EnabledFinchSetup />
            </Grid>
          </Grid>
        )}
        <Grid container flexWrap="nowrap">
          {finchConnectEnabled && (
            <Typography variant="h1" component="p" fontSize="3.5rem" fontWeight="bolder">
              2
            </Typography>
          )}
          <Grid>
            <PayrollConnectionHeading />
            {!isPayrollConnected && (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Connect your payroll system so Alice can automatically administer commuter benefits.
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  By giving Alice 3rd party admin access, we will be able to:
                </Typography>
                <Typography variant="body2" color="text.secondary" component="ul" gutterBottom>
                  <li>View your employee roster</li>
                  <li>Automate sending benefits enrollment notices</li>
                  <li>Set deductions & reimbursements each pay period</li>
                  <li>Terminate benefits when employees are terminated</li>
                </Typography>
              </>
            )}

            <PayrollConnectionInputs />
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

export const PayrollInstructions = () => {
  const org = useOrg()
  const finchConnectEnabled = useIsFinchOnboardingEnabledForPlatform(org)
  if (!org) return null

  const isPayrollConnected = org.isOnboarded || Boolean(org.payrollConnection.payrollConnectedAt)
  const payrollPlatform = org.payrollConnection.payrollPlatform
  const isPremier = payrollPlatform?.code === 'premier_payroll_solutions'

  const accountCreationText = isPayrollConnected
    ? ''
    : isPremier
      ? 'Premier Payroll Solutions will set up the account for you.'
      : 'The account must be created manually by following the linked instructions.'

  return (
    <>
      {finchConnectEnabled ? (
        <>
          <Typography gutterBottom variant="body2">
            Two accounts will be created in {payrollPlatform?.name} for Alice.
          </Typography>
          <Typography gutterBottom variant="body2">
            The first will be automatically created when you connect through our payroll partner, Finch. This
            account is used for viewing payroll information.
          </Typography>
          <Typography gutterBottom variant="body2">
            The second account is used for recording benefit elections and reimbursements.
            {` ${accountCreationText}`}
          </Typography>
        </>
      ) : (
        <Typography gutterBottom variant="body2">
          This step allows Alice to administer benefits for your employees automatically by creating an
          account in {payrollPlatform?.name} for Alice.{` ${accountCreationText}`}
        </Typography>
      )}
      <Typography gutterBottom variant="body2">
        You can connect one payroll account now. If you have others, you may connect them later.
      </Typography>

      {finchConnectEnabled ||
        (!isPremier && (
          <>
            <Typography variant="subtitle1" fontWeight="bold">
              Requirements
            </Typography>
            <List dense>
              <ListItem disableGutters>
                <ListItemIcon>
                  <CheckCircleOutlineIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Login credentials for your payroll provider" />
              </ListItem>
            </List>
          </>
        ))}

      {finchConnectEnabled && (
        <Box mt={3}>
          <Typography variant="subtitle1" fontWeight="bold">
            Good to know
          </Typography>
          <List dense>
            <ListItem disableGutters>
              <ListItemIcon>
                <LightbulbOutlinedIcon fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    Alice works with{' '}
                    <ExternalLink href="https://www.tryfinch.com/" target="_blank">
                      Finch
                    </ExternalLink>
                    , a leading provider of HRIS integrations, for read access.
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </Box>
      )}
    </>
  )
}
